"use client";

import { useId, useEffect, useState } from "react";
import Select, { type StylesConfig } from "react-select";
import countries from "i18n-iso-countries";
import ptLocale from "i18n-iso-countries/langs/pt.json";
import { LANGUAGES, DEFAULT_LANGUAGE } from "@/lib/languages";

countries.registerLocale(ptLocale);

const LANG_TO_COUNTRY: Record<string, string> = Object.fromEntries(
  LANGUAGES.map(l => [l.polylangCode, l.polylangLocale.split("-")[1] ?? ""])
);

type Option = { value: string; label: string };

const OPTIONS: Option[] = Object.entries(
  countries.getNames("pt", { select: "official" })
)
  .map(([code, name]) => ({ value: code, label: name }))
  .sort((a, b) => a.label.localeCompare(b.label, "pt"));

const makeStyles = (invalid: boolean): StylesConfig<Option> => ({
  control: (base, state) => ({
    ...base,
    minHeight: "42px",
    height: "42px",
    borderRadius: "9999px",
    backgroundColor: "white",
    borderColor: invalid ? "transparent" : state.isFocused ? "transparent" : "#d9d9d9",
    boxShadow: invalid
      ? "0 0 0 2px rgb(220 38 38)"
      : state.isFocused
        ? "0 0 0 2px black"
        : "none",
    "&:hover": { borderColor: invalid ? "transparent" : "#d9d9d9" },
    paddingLeft: "8px",
    cursor: "pointer",
    overflow: "visible",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "42px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#00000066",
    paddingRight: "12px",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    border: "1px solid #d9d9d9",
  }),
  menuList: (base) => ({
    ...base,
    padding: "4px 0",
    maxHeight: "220px",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "14px",
    backgroundColor: state.isSelected
      ? "rgba(0,0,0,0.08)"
      : state.isFocused
        ? "rgba(0,0,0,0.04)"
        : "white",
    color: "black",
    cursor: "pointer",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "inherit",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "inherit",
    color: "rgba(0,0,0,0.4)",
  }),
});

export interface CountrySelectProps {
  value: string;
  onChange: (code: string) => void;
  invalid?: boolean;
  label?: React.ReactNode;
  lang?: string;
  required?: boolean;
  showRequiredMark?: boolean;
  id?: string;
}

export function CountrySelect({
  value,
  onChange,
  invalid = false,
  label,
  lang,
  required,
  showRequiredMark = true,
  id,
}: CountrySelectProps) {
  const autoId = useId();
  const inputId = id ?? autoId;

  const [selected, setSelected] = useState<Option | null>(() => {
    if (value) return OPTIONS.find(o => o.value === value) ?? null;
    const siteLang = lang ?? DEFAULT_LANGUAGE.polylangCode;
    const defaultCode = LANG_TO_COUNTRY[siteLang] ?? "";
    return OPTIONS.find(o => o.value === defaultCode) ?? null;
  });

  useEffect(() => {
    if (!value && selected) onChange(selected.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value) setSelected(OPTIONS.find(o => o.value === value) ?? null);
  }, [value]);

  return (
    <div className="flex flex-col space-y-2.5">
      {label && (
        <label htmlFor={inputId} className="inline-flex items-center">
          {label}
          {required && showRequiredMark && (
            <span aria-hidden="true" className="text-fuji-red leading-none relative -top-[1px]">
              *
            </span>
          )}
        </label>
      )}
      <Select
        inputId={inputId}
        options={OPTIONS}
        value={selected}
        instanceId={inputId}
        onChange={opt => {
          const single = opt as Option | null;
          setSelected(single);
          onChange(single?.value ?? "");
        }}
        styles={makeStyles(invalid)}
        placeholder="Selecionar país…"
        isSearchable
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
      />
    </div>
  );
}